import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { customersApi, tableViewsApi, usersApi, teamsApi, triggerBlobDownload, placetelFilename, PLACETEL_HINT } from "@/lib/api";
import { loadFormDraft, saveFormDraft, clearFormDraft } from "@/hooks/useFormDraft";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/components/SimpleToast";
import { MobileFab } from "@/components/MobileFab";
import { CustomerFormDialog, CustomerImportDialog } from "@/components/customer";
import { DataTable } from "@/components/data-table/DataTable";
import { CustomerKanban } from "@/components/customer/CustomerKanban";
import { StatusBadge } from "@/components/data-table/StatusBadge";
import { EmptyState } from "@/components/ListStates";
import { ColumnToggleMenu, SavedViewsMenu, FilterChips } from "@/components/data-table/TableControls";
import {
  Plus, Building2, Loader2, Download, Upload, Search, Eye, Pencil, Trash2,
  StickyNote, Bell, Users as UsersIcon, X, Tag, Wrench, Phone, Table2, Columns3,
} from "lucide-react";

const CUSTOMER_DRAFT_KEY = "draft:customer:new";
const CATEGORIES = ["Kunde", "Interessent", "Lieferant"];

const EMPTY_FORM = {
  company_name: "", category: "Kunde", has_maintenance_contract: false, systems: ["Quincy"],
  address: "", city: "", postal_code: "", phone: "", phone_extension: "", email: "", notes: "", contacts: [],
  phones: [], emails: [],
  assigned_to: "", team_id: "",
};

const customerDraftHasContent = (d) =>
  !!d && !!(d.company_name || d.notes || d.address || d.city || d.postal_code || d.phone || d.email ||
    (Array.isArray(d.contacts) && d.contacts.length > 0));

const DEFAULT_FILTERS = { search: "", category: "all", maintenance: "all", notes: "all", reminders_only: false };
const DEFAULT_PREFS = {
  columns: ["company_name", "category", "city", "phone", "email", "contacts"],
  sort_by: "company_name", sort_dir: "asc", page_size: 25, filters: DEFAULT_FILTERS, view_mode: "table",
};
const LS_PREFS = "tableprefs:customers";

function urlCategory() {
  try {
    const c = new URLSearchParams(window.location.search).get("category");
    return c && CATEGORIES.includes(c) ? c : null;
  } catch { return null; }
}

function loadInitialPrefs() {
  const cat = urlCategory();
  try {
    const cached = JSON.parse(localStorage.getItem(LS_PREFS) || "null");
    if (cached) {
      const merged = { ...DEFAULT_PREFS, ...cached, filters: { ...DEFAULT_FILTERS, ...(cached.filters || {}), search: "" } };
      if (cat) merged.filters.category = cat;
      return merged;
    }
  } catch (e) { console.debug("Prefs-Cache:", e); }
  return cat ? { ...DEFAULT_PREFS, filters: { ...DEFAULT_FILTERS, category: cat } } : DEFAULT_PREFS;
}

export default function Customers() {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkCategory = useRef(urlCategory());

  // ---- table prefs (per user, server-persisted with localStorage fallback) ----
  const [prefs, setPrefsState] = useState(loadInitialPrefs);
  const [views, setViews] = useState([]);
  const [activeViewId, setActiveViewId] = useState(null);
  const saveTimer = useRef(null);

  const persistPrefs = useCallback((next) => {
    // Freien Suchtext NICHT dauerhaft speichern (vermeidet „leere Liste"-Falle
    // durch einen alten Suchbegriff nach Reload). Filter/Spalten/Sortierung bleiben.
    const toSave = { ...next, filters: { ...(next.filters || {}), search: "" } };
    try { localStorage.setItem(LS_PREFS, JSON.stringify(toSave)); } catch (e) { console.debug("Prefs speichern:", e); }
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      tableViewsApi.savePrefs("customers", toSave).catch((e) => console.debug("Prefs-Sync:", e));
    }, 700);
  }, []);

  const setPrefs = useCallback((updater) => {
    setPrefsState((prev) => {
      const next = typeof updater === "function" ? updater(prev) : { ...prev, ...updater };
      persistPrefs(next);
      return next;
    });
  }, [persistPrefs]);

  useEffect(() => {
    tableViewsApi.get("customers").then(({ data }) => {
      if (data.prefs && Object.keys(data.prefs).length > 0) {
        const cat = deepLinkCategory.current;
        setPrefsState((prev) => ({ ...prev, ...data.prefs, filters: { ...DEFAULT_FILTERS, ...(data.prefs.filters || {}), search: "", ...(cat ? { category: cat } : {}) } }));
      }
      setViews(Array.isArray(data.views) ? data.views : []);
    }).catch((e) => console.debug("Ansichten laden:", e));
  }, []);

  // ---- data ----
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selection, setSelection] = useState(new Set());
  const [reminderIds, setReminderIds] = useState([]);
  const [searchInput, setSearchInput] = useState(prefs.filters.search || "");

  // dialogs / form
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingPlacetel, setExportingPlacetel] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editCustomer, setEditCustomer] = useState(null);
  const [draftRestored, setDraftRestored] = useState(false);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [custAutosave, setCustAutosave] = useState("idle");
  const [custAutosaveTime, setCustAutosaveTime] = useState(null);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);

  useEffect(() => {
    usersApi.getAll().then((r) => setUsers(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    teamsApi.getAll().then((r) => setTeams(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const f = prefs.filters;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    const kanban = prefs.view_mode === "kanban";
    try {
      const { data } = await customersApi.getPaged({
        search: f.search || undefined,
        category: kanban ? "all" : f.category, maintenance: f.maintenance, notes: f.notes,
        reminders_only: f.reminders_only,
        sort_by: prefs.sort_by, sort_dir: prefs.sort_dir,
        page: kanban ? 1 : page, page_size: kanban ? 500 : prefs.page_size,
      });
      setRows(Array.isArray(data.items) ? data.items : []);
      setTotal(data.total || 0);
    } catch (e) {
      console.debug("Kunden konnten nicht geladen werden", e);
      setError("Die Kundenliste konnte nicht geladen werden. Bitte prüfen Sie Ihre Verbindung.");
      setRows([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.search, f.category, f.maintenance, f.notes, f.reminders_only, prefs.sort_by, prefs.sort_dir, prefs.page_size, prefs.view_mode, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    customersApi.getWithReminders().then(({ data }) => setReminderIds(data.customer_ids || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const onSync = () => fetchData();
    window.addEventListener("offline-sync-complete", onSync);
    return () => window.removeEventListener("offline-sync-complete", onSync);
  }, [fetchData]);

  // debounce search -> filters.search
  useEffect(() => {
    const t = setTimeout(() => {
      if ((f.search || "") !== searchInput) { setFilters({ search: searchInput }); }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // voice / quick-create / KPI deep links
  useEffect(() => {
    const q = searchParams.get("q");
    const isNew = searchParams.get("new");
    const category = searchParams.get("category");
    if (q) setSearchInput(q);
    if (isNew) handleOpenDialog(null);
    if (category && CATEGORIES.includes(category)) setFilters({ category });
    if (q || isNew || category) { searchParams.delete("q"); searchParams.delete("new"); searchParams.delete("category"); setSearchParams(searchParams, { replace: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setFilters = (patch) => { setPrefs((p) => ({ ...p, filters: { ...p.filters, ...patch } })); setPage(1); setActiveViewId(null); };
  const onSort = (key) => setPrefs((p) => ({ ...p, sort_by: key, sort_dir: p.sort_by === key && p.sort_dir === "asc" ? "desc" : "asc" }));
  const setPageSize = (n) => setPrefs((p) => ({ ...p, page_size: n }));
  const setColumns = (cols) => setPrefs((p) => ({ ...p, columns: cols }));

  // ---- selection ----
  const toggleRow = (id) => setSelection((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelection((prev) => { const allIds = rows.map((r) => r.id); const every = allIds.every((id) => prev.has(id)); const n = new Set(prev); every ? allIds.forEach((id) => n.delete(id)) : allIds.forEach((id) => n.add(id)); return n; });
  const clearSelection = () => setSelection(new Set());

  // Inline-Kategorie ändern (Tabelle & Kanban), optimistisch
  const changeCategory = async (id, category) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, category } : r)));
    try { await customersApi.bulkUpdate([id], category, null); toast.success(`Auf „${category}" gesetzt`); }
    catch { toast.error("Änderung fehlgeschlagen"); fetchData(); }
  };
  const setViewMode = (mode) => { clearSelection(); setPrefs((p) => ({ ...p, view_mode: mode })); };

  // ---- filter chips ----
  const chips = [];
  if (f.category !== "all") chips.push({ key: "category", label: `Kategorie: ${f.category}` });
  if (f.maintenance !== "all") chips.push({ key: "maintenance", label: `Wartung: ${f.maintenance === "yes" ? "Ja" : "Nein"}` });
  if (f.notes !== "all") chips.push({ key: "notes", label: `Notizen: ${f.notes === "yes" ? "mit" : "ohne"}` });
  if (f.reminders_only) chips.push({ key: "reminders_only", label: "Nur mit Erinnerung" });
  const removeChip = (key) => setFilters({ [key]: key === "reminders_only" ? false : "all" });
  const clearAllFilters = () => { setSearchInput(""); setPrefs((p) => ({ ...p, filters: { ...DEFAULT_FILTERS } })); setPage(1); setActiveViewId(null); };

  // ---- columns ----
  const columns = [
    { key: "company_name", header: "Firma", sortable: true, render: (r) => (
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0"><Building2 className="w-4 h-4" /></div>
          <span className="font-medium truncate">{r.company_name}</span>
        </div>
      ) },
    { key: "category", header: "Kategorie", sortable: true, render: (r) => (
        <span onClick={(e) => e.stopPropagation()} className="inline-block" data-testid={`inline-cat-wrap-${r.id}`}>
          <Select value={r.category || "Kunde"} onValueChange={(v) => changeCategory(r.id, v)}>
            <SelectTrigger className="h-7 w-[130px] text-xs" data-testid={`inline-cat-${r.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </span>
      ) },
    { key: "city", header: "Ort", sortable: true, render: (r) => r.city ? <span>{r.postal_code ? `${r.postal_code} ` : ""}{r.city}</span> : <span className="text-muted-foreground">—</span> },
    { key: "phone", header: "Telefon", render: (r) => r.phone || <span className="text-muted-foreground">—</span> },
    { key: "email", header: "E-Mail", sortable: true, render: (r) => r.email ? <span className="truncate max-w-[220px] inline-block align-middle">{r.email}</span> : <span className="text-muted-foreground">—</span> },
    { key: "contacts", header: "Ansprechpartner", render: (r) => (
        <span className="inline-flex items-center gap-1 text-sm"><UsersIcon className="w-3.5 h-3.5 text-muted-foreground" />{(r.contacts || []).length}</span>
      ) },
    { key: "maintenance", header: "Wartung", render: (r) => r.has_maintenance_contract
        ? <StatusBadge status="aktiv" label="Wartung" tone="success" icon={Wrench} />
        : <span className="text-muted-foreground text-sm">—</span> },
    { key: "notes", header: "Notizen", render: (r) => (r.notes && r.notes.trim())
        ? <StickyNote className="w-4 h-4 text-warning" aria-label="hat Notizen" /> : <span className="text-muted-foreground">—</span> },
    { key: "reminder", header: "Erinnerung", render: (r) => reminderIds.includes(r.id)
        ? <StatusBadge status="ueberfaellig" label="Erinnerung" tone="danger" icon={Bell} /> : <span className="text-muted-foreground">—</span> },
  ];

  const rowActions = (row) => [
    { label: "Anzeigen", icon: Eye, onClick: () => navigate(`/customers/${row.id}`) },
    { label: "Bearbeiten", icon: Pencil, onClick: () => handleOpenDialog(row) },
    { label: "Löschen", icon: Trash2, danger: true, separatorBefore: true, onClick: () => handleDeleteWithUndo(row) },
  ];

  // ---- create/edit ----
  function handleOpenDialog(customer) {
    if (customer) {
      setEditCustomer(customer);
      setFormData({
        company_name: customer.company_name, category: customer.category || "Kunde",
        has_maintenance_contract: customer.has_maintenance_contract || false,
        systems: customer.systems && customer.systems.length ? customer.systems : ["Quincy"],
        address: customer.address || "", city: customer.city || "", postal_code: customer.postal_code || "",
        phone: customer.phone || "", phone_extension: customer.phone_extension || "",
        email: customer.email || "", notes: customer.notes || "", contacts: customer.contacts || [],
        phones: (customer.phones && customer.phones.length) ? customer.phones : (customer.phone ? [{ label: "", value: customer.phone }] : []),
        emails: (customer.emails && customer.emails.length) ? customer.emails : (customer.email ? [{ label: "", value: customer.email }] : []),
        assigned_to: customer.assigned_to || "", team_id: customer.team_id || "",
      });
    } else {
      setEditCustomer(null);
      setCustAutosave("idle");
      setCustAutosaveTime(null);
      const draft = loadFormDraft(CUSTOMER_DRAFT_KEY);
      if (customerDraftHasContent(draft)) {
        setFormData({ ...EMPTY_FORM, ...draft, contacts: Array.isArray(draft.contacts) ? draft.contacts : [] });
        setDraftRestored(true);
      } else { setDraftRestored(false); setFormData({ ...EMPTY_FORM }); }
      // Gerätübergreifenden DB-Entwurf nachladen (überschreibt nur, wenn er Inhalt hat)
      customersApi.getDraft().then(({ data }) => {
        if (data?.data && customerDraftHasContent(data.data)) {
          setFormData({ ...EMPTY_FORM, ...data.data, contacts: Array.isArray(data.data.contacts) ? data.data.contacts : [] });
          setDraftRestored(true);
          if (data.updated_at) { setCustAutosaveTime(new Date(data.updated_at)); setCustAutosave("saved"); }
        }
      }).catch(() => {});
    }
    setDialogOpen(true);
  }

  useEffect(() => {
    if (!dialogOpen || editCustomer) return;
    if (!customerDraftHasContent(formData)) return;
    saveFormDraft(CUSTOMER_DRAFT_KEY, formData);
    setCustAutosave("saving");
    const t = setTimeout(async () => {
      try {
        const res = await customersApi.saveDraft(formData);
        setCustAutosave("saved");
        setCustAutosaveTime(res.data?.updated_at ? new Date(res.data.updated_at) : new Date());
      } catch { setCustAutosave("offline"); }
    }, 1200);
    return () => clearTimeout(t);
  }, [dialogOpen, editCustomer, formData]);

  const handleDiscardCustomerDraft = () => {
    clearFormDraft(CUSTOMER_DRAFT_KEY);
    customersApi.deleteDraft().catch(() => {});
    setDraftRestored(false);
    setFormData({ ...EMPTY_FORM });
    setCustAutosave("idle");
    setCustAutosaveTime(null);
    toast.info("Entwurf verworfen");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editCustomer) {
        const res = await customersApi.update(editCustomer.id, formData);
        if (res?._queued) toast.info("Offline gespeichert – wird synchronisiert"); else toast.success("Kunde aktualisiert");
      } else {
        const res = await customersApi.create(formData);
        if (res?._queued) toast.info("Offline gespeichert – wird synchronisiert"); else toast.success("Kunde erstellt");
        clearFormDraft(CUSTOMER_DRAFT_KEY); setDraftRestored(false);
        customersApi.deleteDraft().catch(() => {});
        setCustAutosave("idle"); setCustAutosaveTime(null);
      }
      setDialogOpen(false); fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || "Fehler beim Speichern"); }
    finally { setSaving(false); }
  };

  const handleDeleteWithUndo = (customer) => {
    setRows((prev) => prev.filter((c) => c.id !== customer.id));
    setTotal((t) => Math.max(0, t - 1));
    toast.action("Kunde gelöscht", {
      actionLabel: "Rückgängig", duration: 5000,
      onAction: () => fetchData(),
      onTimeout: async () => { try { await customersApi.delete(customer.id); } catch { toast.error("Fehler beim Löschen"); fetchData(); } },
    });
  };

  // ---- bulk ----
  const bulkCategory = async (category) => {
    const ids = Array.from(selection);
    try { await customersApi.bulkUpdate(ids, category, null); toast.success(`${ids.length} auf „${category}" gesetzt`); clearSelection(); fetchData(); }
    catch { toast.error("Massenaktion fehlgeschlagen"); }
  };
  const bulkMaintenance = async (val) => {
    const ids = Array.from(selection);
    try { await customersApi.bulkUpdate(ids, null, val); toast.success(`Wartung für ${ids.length} aktualisiert`); clearSelection(); fetchData(); }
    catch { toast.error("Massenaktion fehlgeschlagen"); }
  };
  const bulkDelete = async () => {
    const ids = Array.from(selection);
    setRows((prev) => prev.filter((c) => !selection.has(c.id)));
    setTotal((t) => Math.max(0, t - ids.length));
    clearSelection();
    toast.action(`${ids.length} Kunden gelöscht`, {
      actionLabel: "Rückgängig", duration: 5000,
      onAction: () => fetchData(),
      onTimeout: async () => { try { await customersApi.bulkDelete(ids); } catch { toast.error("Löschen fehlgeschlagen"); } finally { fetchData(); } },
    });
  };

  // ---- import/export ----
  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const response = await customersApi.exportCsv();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a"); link.href = url;
      link.setAttribute("download", `kunden_export_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
      toast.success("Export erfolgreich heruntergeladen");
    } catch { toast.error("Export fehlgeschlagen"); } finally { setExporting(false); }
  };
  const handleExportPlacetel = async () => {
    setExportingPlacetel(true);
    try {
      const params = {};
      if (f.search) params.search = f.search;
      const response = await customersApi.exportPlacetel(params);
      triggerBlobDownload(response.data, placetelFilename());
      toast.success(PLACETEL_HINT, { duration: 8000 });
    } catch { toast.error("Placetel-Export fehlgeschlagen"); } finally { setExportingPlacetel(false); }
  };
  const handleImportCsv = async () => {
    if (!importFile) { toast.error("Bitte wählen Sie eine CSV-Datei aus"); return; }
    setImporting(true);
    try {
      const response = await customersApi.importCsv(importFile, updateExisting);
      const result = response.data; const msg = [];
      if (result.imported > 0) msg.push(`${result.imported} importiert`);
      if (result.updated > 0) msg.push(`${result.updated} aktualisiert`);
      if (result.skipped > 0) msg.push(`${result.skipped} übersprungen`);
      if (result.imported > 0 || result.updated > 0) { toast.success(`Import: ${msg.join(", ")}`); fetchData(); }
      else toast.error(`Keine Kunden importiert. ${result.skipped} übersprungen.`);
      setImportDialogOpen(false); setImportFile(null); setUpdateExisting(false);
    } catch (error) { toast.error(error.response?.data?.detail || "Import fehlgeschlagen"); } finally { setImporting(false); }
  };
  const cancelImport = () => { setImportDialogOpen(false); setImportFile(null); setUpdateExisting(false); };

  // ---- saved views ----
  const applyView = (v) => {
    const c = v.config || {};
    const next = { ...prefs, ...c, filters: { ...DEFAULT_FILTERS, ...(c.filters || {}) } };
    setPrefsState(next);
    persistPrefs(next);
    setSearchInput(c.filters?.search || ""); setPage(1); setActiveViewId(v.id);
  };
  const saveCurrentView = async (name) => {
    try {
      const { data } = await tableViewsApi.addView("customers", name, {
        filters: prefs.filters, columns: prefs.columns, sort_by: prefs.sort_by, sort_dir: prefs.sort_dir, page_size: prefs.page_size,
      });
      setViews((prev) => [...prev, data]); setActiveViewId(data.id); toast.success(`Ansicht „${name}" gespeichert`);
    } catch { toast.error("Ansicht konnte nicht gespeichert werden"); }
  };
  const deleteView = async (id) => {
    try { await tableViewsApi.deleteView("customers", id); setViews((prev) => prev.filter((v) => v.id !== id)); if (activeViewId === id) setActiveViewId(null); }
    catch { toast.error("Ansicht konnte nicht gelöscht werden"); }
  };

  const selCount = selection.size;

  return (
    <div className="space-y-5 animate-fade-in" data-testid="customers-page">
      <MobileFab onClick={() => handleOpenDialog(null)} label="Neuer Kunde" testid="fab-new-customer" />

      {/* Seitenkopf */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Kunden</h1>
          <p className="text-muted-foreground text-sm mt-1" data-testid="customers-count">
            {loading ? "Wird geladen…" : `${total} Einträge · Kunden, Interessenten & Lieferanten`}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => setImportDialogOpen(true)} data-testid="import-csv-btn"><Upload className="w-4 h-4 mr-2" />Import</Button>
          <Button variant="outline" size="sm" onClick={handleExportCsv} disabled={exporting} data-testid="export-csv-btn">{exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}Export</Button>
          <Button variant="outline" size="sm" onClick={handleExportPlacetel} disabled={exportingPlacetel} data-testid="export-placetel-btn">{exportingPlacetel ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Phone className="w-4 h-4 mr-2" />}Placetel-Export</Button>
          <Button size="sm" onClick={() => handleOpenDialog(null)} data-testid="add-customer-btn"><Plus className="w-4 h-4 mr-2" />Neuer Kunde</Button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Firma, Ort, E-Mail oder Telefon…" className="pl-9" data-testid="customers-search" />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={f.category} onValueChange={(v) => setFilters({ category: v })}>
              <SelectTrigger className="h-9 w-[150px]" data-testid="filter-category"><SelectValue placeholder="Kategorie" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Kategorien</SelectItem>
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={f.maintenance} onValueChange={(v) => setFilters({ maintenance: v })}>
              <SelectTrigger className="h-9 w-[130px]" data-testid="filter-maintenance"><SelectValue placeholder="Wartung" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Wartung: alle</SelectItem>
                <SelectItem value="yes">Mit Wartung</SelectItem>
                <SelectItem value="no">Ohne Wartung</SelectItem>
              </SelectContent>
            </Select>
            <Select value={f.notes} onValueChange={(v) => setFilters({ notes: v })}>
              <SelectTrigger className="h-9 w-[130px]" data-testid="filter-notes"><SelectValue placeholder="Notizen" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Notizen: alle</SelectItem>
                <SelectItem value="yes">Mit Notizen</SelectItem>
                <SelectItem value="no">Ohne Notizen</SelectItem>
              </SelectContent>
            </Select>
            <Button variant={f.reminders_only ? "default" : "outline"} size="sm" onClick={() => setFilters({ reminders_only: !f.reminders_only })} data-testid="filter-reminders">
              <Bell className="w-4 h-4 mr-1.5" />Erinnerungen
            </Button>
            <ColumnToggleMenu columns={columns} visibleColumns={prefs.columns} onChange={setColumns} requiredKeys={["company_name"]} />
            <SavedViewsMenu views={views} activeViewId={activeViewId} onApply={applyView} onSave={saveCurrentView} onDelete={deleteView} />
            <div className="flex items-center rounded-md border border-border p-0.5" data-testid="view-toggle">
              <Button variant={prefs.view_mode !== "kanban" ? "secondary" : "ghost"} size="sm" className="h-8 px-2.5" onClick={() => setViewMode("table")} title="Tabelle" data-testid="view-table-btn"><Table2 className="w-4 h-4" /></Button>
              <Button variant={prefs.view_mode === "kanban" ? "secondary" : "ghost"} size="sm" className="h-8 px-2.5" onClick={() => setViewMode("kanban")} title="Kanban" data-testid="view-kanban-btn"><Columns3 className="w-4 h-4" /></Button>
            </div>
          </div>
        </div>
        <FilterChips chips={chips} onRemove={removeChip} onClearAll={clearAllFilters} />
      </div>

      {/* Sammelaktionen */}
      {selCount > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-4 py-2.5" data-testid="bulk-action-bar">
          <span className="text-sm font-medium">{selCount} ausgewählt</span>
          <div className="h-4 w-px bg-border mx-1" />
          <DropdownMenu>
            <DropdownMenuTrigger asChild><Button variant="outline" size="sm" data-testid="bulk-category-btn"><Tag className="w-4 h-4 mr-1.5" />Kategorie</Button></DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuLabel>Kategorie setzen</DropdownMenuLabel><DropdownMenuSeparator />
              {CATEGORIES.map((c) => <DropdownMenuItem key={c} onClick={() => bulkCategory(c)} data-testid={`bulk-cat-${c}`}>{c}</DropdownMenuItem>)}
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild><Button variant="outline" size="sm" data-testid="bulk-maintenance-btn"><Wrench className="w-4 h-4 mr-1.5" />Wartung</Button></DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => bulkMaintenance(true)} data-testid="bulk-maint-yes">Wartung aktivieren</DropdownMenuItem>
              <DropdownMenuItem onClick={() => bulkMaintenance(false)} data-testid="bulk-maint-no">Wartung entfernen</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" onClick={bulkDelete} data-testid="bulk-delete-btn"><Trash2 className="w-4 h-4 mr-1.5" />Löschen</Button>
          <Button variant="ghost" size="sm" onClick={clearSelection} data-testid="bulk-clear-btn"><X className="w-4 h-4 mr-1.5" />Auswahl aufheben</Button>
        </div>
      )}

      {/* Ansicht: Tabelle oder Kanban */}
      {prefs.view_mode === "kanban" ? (
        loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : rows.length === 0 ? (
          <EmptyState icon={Building2} title="Keine Kunden gefunden" description={chips.length || f.search ? "Passen Sie Suche oder Filter an." : "Legen Sie Ihren ersten Kunden an."} />
        ) : (
          <CustomerKanban rows={rows} categories={CATEGORIES} onChangeCategory={changeCategory} onOpen={(cid) => navigate(`/customers/${cid}`)} />
        )
      ) : (
      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        sort={{ by: prefs.sort_by, dir: prefs.sort_dir }}
        onSort={onSort}
        visibleColumns={prefs.columns}
        selection={selection}
        onToggleRow={toggleRow}
        onToggleAll={toggleAll}
        onRowClick={(row) => navigate(`/customers/${row.id}`)}
        rowActions={rowActions}
        page={page}
        pageSize={prefs.page_size}
        total={total}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        testid="customers-table"
        error={error}
        onRetry={fetchData}
        mobileCard={(r) => (
          <div>
            <div className="flex items-center gap-2">
              <p className="font-semibold truncate">{r.company_name}</p>
              <StatusBadge status={r.category || "Kunde"} />
            </div>
            <div className="mt-1 text-sm text-muted-foreground space-y-0.5">
              {(r.city || r.postal_code) && <p className="truncate">{r.postal_code ? `${r.postal_code} ` : ""}{r.city}</p>}
              {r.phone && <p className="truncate">{r.phone}</p>}
              {r.email && <p className="truncate">{r.email}</p>}
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {r.has_maintenance_contract && <StatusBadge status="aktiv" label="Wartung" tone="success" icon={Wrench} />}
              {reminderIds.includes(r.id) && <StatusBadge status="aktiv" label="Erinnerung" tone="warning" icon={Bell} />}
            </div>
          </div>
        )}
        emptyState={
          <EmptyState
            icon={Building2}
            title="Keine Kunden gefunden"
            description={chips.length || f.search ? "Passen Sie Suche oder Filter an." : "Legen Sie Ihren ersten Kunden an."}
            action={!(chips.length || f.search) && <Button onClick={() => handleOpenDialog(null)} data-testid="empty-add-btn"><Plus className="w-4 h-4 mr-2" />Ersten Kunden anlegen</Button>}
          />
        }
      />
      )}

      <CustomerFormDialog open={dialogOpen} onOpenChange={setDialogOpen} editCustomer={editCustomer} formData={formData} setFormData={setFormData} categories={CATEGORIES} onSubmit={handleSubmit} saving={saving} draftRestored={draftRestored} onDiscardDraft={handleDiscardCustomerDraft} users={users} teams={teams} autosaveState={custAutosave} autosaveTime={custAutosaveTime} />
      <CustomerImportDialog open={importDialogOpen} onOpenChange={setImportDialogOpen} importFile={importFile} setImportFile={setImportFile} updateExisting={updateExisting} setUpdateExisting={setUpdateExisting} importing={importing} onImport={handleImportCsv} onCancel={cancelImport} />
    </div>
  );
}
